import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import {
    GET_DEMO_FORM_DETAILS,
    GET_BENCHMARK_FORM_DETAILS,
    GET_DEMO_SCATTER_DATA,
    GET_BENCHMARK_SCATTER_DATA,
    GET_DEMO_TABLE_DATA,
    GET_BENCHMARK_TABLE_DATA,
    GET_DEMO_TRENDLINE_AND_HEATMAP_DATA,
    GET_BENCHMARK_TRENDLINE_AND_HEATMAP_DATA,
    GET_DEMO_BENCHMARK_ALL,
    GET_BENCHMARK_BENCHMARK_ALL,
    GET_BENCHMARK_HEATMAP_VARIATIONS,
    GET_DEMO_HEATMAP_VARIATIONS,
} from 'Apollo/Queries';

import Header from './js/Header';
import ScatterPlotWrapper from './js/ScatterPlotWrapper';
import SpecialScatterPlot from './js/d3/SpecialScatterPlot';
import TrendLineWrapper from './js/TrendLineWrapper';
import HeatmapWrapper from './js/HeatmapWrapper';
import TableWrapper from './js/TableWrapper';
import Widget from './js/Widget';
import Loader from './js/Loader';
import './scss/index.scss';
import SelectedMdrms from './js/SelectedMdrms';
import Markdown from 'markdown-to-jsx';
import BenchmarkScatterPlot from './js/d3/BenchmarkScatterPlot';
import {
    TableSkeleton,
    ScatterPlotSkeleton,
    HeatmapSkeleton,
    TrendLineSkeleton,
    DashboardSkeleton,
} from '../../common/Skeleton';
import { exportToCSV, exportToXLS } from './js/utils/exportTableData';
import { BENCHMARK, VARIATIONS } from './js/utils/Constants';

const getAnalyzeNarrativeMutation = (isBenchmark) => gql`
  mutation analyzeNarrative(
    $rssid: String!
    $currentQuarterCombination: [String!]!
    $reportingForm: String!
  ) {
    ${isBenchmark ? 'BENCHMARKanalyzeAnomalyNarrative' : 'DEMOanalyzeAnomalyNarrative'}(
      input: {
        rssid: $rssid
        currentQuarterCombination: $currentQuarterCombination
        reportingForm: $reportingForm
      }
    )
  }
`;

const ReportDashboard = ({ dataSource = 'DEMO' }) => {
    const [reportType, setReportType] = useState('variations');
    const [valueMode, setValueMode] = useState('regular');
    const [reportingForm, setReportingForm] = useState('FR Y-9C');
    const [institution, setInstitution] = useState(null);
    const [startQuarter, setStartQuarter] = useState(null);
    const [endQuarter, setEndQuarter] = useState(null);

    const [queryParams, setQueryParams] = useState(null);
    const isInitialized = !!queryParams;

    const [tableData, setTableData] = useState([]);
    const [scatterData, setScatterData] = useState([]);
    const [trendData, setTrendData] = useState([]);
    const [heatmapData, setHeatmapData] = useState([]);
    const [selectedMDRM, setSelectedMDRM] = useState(null);
    const [selectMode, setSelectMode] = useState(false);
    const [selectedMdrms, setSelectedMdrms] = useState([]);
    const [insightMdrm, setInsightMdrm] = useState(null);
    const [mdrmTyping, setMdrmTyping] = useState('');
    const [typingIndex, setTypingIndex] = useState(0);
    const [loadingTimeout, setLoadingTimeout] = useState(false);
    const [mdrmResponse, setMdrmResponse] = useState(null);
    const [benchmarkQuarter, setBenchmarkQuarter] = useState('');
    const [benchmarkRSSDs, setBenchmarkRSSDs] = useState([]);
    const [benchmarkTableData, setBenchmarkTableData] = useState([]);
    const [benchmarkInstitutionDetails, setBenchmarkInstitutionDetails] = useState({});
    const [hiddenSchedules, setHiddenSchedules] = useState(new Set());
    const [isHeatmapLoading, setIsHeatmapLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isTableLoading, setIsTableLoading] = useState(false);
    const [searchKeys, setSearchKeys] = useState([]);
    const [heatmapColumns, setHeatmapColumns] = useState([]);
    const [heatmapChartData, setHeatmapChartData] = useState([]);
    const [trendLineChartData, setTrendLineChartData] = useState([]);
    const [isBenchmarkDataLoading, setIsBenchmarkDataLoading] = useState(false);

    const getPrefix = () => dataSource?.toUpperCase?.() || 'DEMO';

    const isBenchmark = dataSource?.toUpperCase() === 'BENCHMARK';

    const FORM_DETAILS_QUERY = isBenchmark
        ? GET_BENCHMARK_FORM_DETAILS
        : GET_DEMO_FORM_DETAILS;
    const SCATTER_QUERY = isBenchmark
        ? GET_BENCHMARK_SCATTER_DATA
        : GET_DEMO_SCATTER_DATA;
    const TABLE_QUERY = isBenchmark ? GET_BENCHMARK_TABLE_DATA : GET_DEMO_TABLE_DATA;
    const TREND_QUERY = isBenchmark
        ? GET_BENCHMARK_TRENDLINE_AND_HEATMAP_DATA
        : GET_DEMO_TRENDLINE_AND_HEATMAP_DATA;
    const BENCHMARK_ALL_QUERY = isBenchmark
        ? GET_BENCHMARK_BENCHMARK_ALL
        : GET_DEMO_BENCHMARK_ALL;
    const HEATMAP_VARIATIONS = isBenchmark
        ? GET_BENCHMARK_HEATMAP_VARIATIONS
        : GET_DEMO_HEATMAP_VARIATIONS;

    const [fetchFormDetails, { data: formMetaData, loading: metaLoading }] =
        useLazyQuery(FORM_DETAILS_QUERY);
    const [fetchBenchmarkData, { data: benchmarkRaw }] =
        useLazyQuery(BENCHMARK_ALL_QUERY);

    const formDetailsKey = `${getPrefix()}getFormDetails`;
    const formDetails = formMetaData?.[formDetailsKey] || {};

    const getDataByKey = (base, suffix) => {
        const key = `${getPrefix()}${suffix}`;
        return base?.[key];
    };

    const [generatedData, setGeneratedData] = useState(null);

    const getGeneratedMarkdown = (mdrm, value) => `
        ${value}
    `;

    const ANALYZE_NARRATIVE_MUTATION = getAnalyzeNarrativeMutation(isBenchmark);
    const [runAnalyzeNarrative] = useMutation(ANALYZE_NARRATIVE_MUTATION);

    const [executeMutation] = useMutation(gql`
        mutation generateQuarterValues($rssid: [String!]!, $mdrm: String!, $quarter: String!, $reportingForm: String!){

            ${isBenchmark ? 'BENCHMARKgenerateQuarterValues' : 'DEMOgenerateQuarterValues'}(
                input: {
                    rssid: $rssid,
                    mdrm: $mdrm,
                    quarter: $quarter,
                    reportingForm: $reportingForm
                }
            )
        }
    `);

    useEffect(() => {
        if (reportingForm) {
            fetchFormDetails({
                variables: { form: reportingForm },
                fetchPolicy: 'network-only',
            });
        }
    }, [reportingForm]);

    const formatQuarter = (q) => (q ? q.toLowerCase().replace(' ', '_') : '');

    useEffect(() => {
        const formKey = getDataByKey(formMetaData, 'getFormDetails');
        if (!formKey) return;

        const defaults = formKey.default || {
            form: reportingForm,
            rssid: '',
            StartQuarter: '',
            EndQuarter: '',
        };

        const institutionValue = defaults.rssid?.split(' - ')[0] || '';
        const startQ = defaults.StartQuarter;
        const endQ = defaults.EndQuarter;

        setInstitution(institutionValue);
        setStartQuarter(startQ);
        setEndQuarter(endQ);

        setQueryParams({
            reportingForm: defaults.form,
            institution: institutionValue,
            startQuarter: startQ,
            endQuarter: endQ,
        });
    }, [formMetaData]);

    const [fetchDemoTableData, { data: demoTableData }] =
        useLazyQuery(GET_DEMO_TABLE_DATA);
    const [fetchBenchmarkTableData, { data: benchmarkTableDataRaw }] = useLazyQuery(
        GET_BENCHMARK_TABLE_DATA,
    );

    const [fetchScatterData, { data: scatterRaw, loading: isScatterLoading }] =
        useLazyQuery(SCATTER_QUERY);
    const [fetchHeatmapTrend, { data: mdrmData }] = useLazyQuery(TREND_QUERY);

    const benchmarkRSSDsRef = useRef(benchmarkRSSDs);
    const fullMarkdownRef = useRef('');
    const typingIntervalRef = useRef(null);
    const [tableNeedsRefresh, setTableNeedsRefresh] = useState(false);

    const handleSearchKeysChange = (keys) => {
        setSearchKeys(keys);
    };

    const loweredKeys = useMemo(
        () => searchKeys.map((k) => k.toLowerCase().trim()).filter(Boolean),
        [searchKeys],
    );

    const typingIndexRef = useRef(0);
    const tData = useQuery(TABLE_QUERY, {
        skip: !isInitialized,
        variables: isInitialized
            ? {
                form: queryParams.reportingForm,
                rssid: queryParams.institution,
                startQuarter: formatQuarter(queryParams.startQuarter),
                endQuarter: formatQuarter(queryParams.endQuarter),
                case: valueMode,
                mdrms: selectedMdrms,
            }
            : {},
    });

    const isTableQueryLoading = tData.loading;

    useEffect(() => {
        const tableKey = `${getPrefix()}tableData`;
        if (tData?.data?.[tableKey]) {
            setTableData(tData.data[tableKey]);
        }
    }, [tData.data]);

    useEffect(() => {
        setSelectedMdrms([]);
        setSelectedMDRM(null);
        setTrendData([]);
        setHeatmapData([]);
    }, [reportType, valueMode, queryParams]);

    useEffect(() => {
        benchmarkRSSDsRef.current = benchmarkRSSDs;
    }, [benchmarkRSSDs]);
    useEffect(() => {
        if (!isInitialized) return;
        fetchScatterData({
            variables: {
                form: queryParams.reportingForm,
                rssid: queryParams.institution,
                startQuarter: formatQuarter(queryParams.startQuarter),
                endQuarter: formatQuarter(queryParams.endQuarter),
                case: valueMode,
            },
        });
    }, [isInitialized, queryParams, valueMode]);

    useEffect(() => {
        const scatterKey = `${getPrefix()}scatterData`;
        if (!scatterRaw?.[scatterKey]) return;
        setScatterData(scatterRaw[scatterKey]);
    }, [scatterRaw]);

    useEffect(() => {
        if (!isInitialized || selectedMdrms.length === 0) return;
        fetchHeatmapTrend({
            variables: {
                form: queryParams.reportingForm,
                mdrms: selectedMdrms,
                startQuarter: formatQuarter(queryParams.startQuarter),
                endQuarter: formatQuarter(queryParams.endQuarter),
                interval: 1,
                case: valueMode,
            },
        });
    }, [isInitialized, selectedMdrms, queryParams, valueMode]);

    useEffect(() => {
        const mdrmKey = `${getPrefix()}mdrmData`;
        const mdrmSection = mdrmData?.[mdrmKey];
        if (!mdrmSection) return;

        const { periods, results } = mdrmSection;

        const flatData = results.flatMap((item) => {
            const mdrm = item?.data?.MDRM;
            const color = item?.meta?.Color || '#ccc';
            const changes = item?.data?.PercentageChanges;
            if (!mdrm || !Array.isArray(changes)) return [];
            return changes.map((val, i) => ({
                row: mdrm,
                column: periods[i],
                value: val,
                color,
            }));
        });

        setHeatmapData(flatData);
        setTrendData(flatData);
    }, [mdrmData]);

    useEffect(() => {
        const benchmarkKey = `${getPrefix()}benchmarkAll`;
        const res = benchmarkRaw?.[benchmarkKey];

        if (!res) return;

        setBenchmarkTableData(res.results || []);
        setBenchmarkInstitutionDetails(res.institution_details || {});

        setIsBenchmarkDataLoading(false);
    }, [benchmarkRaw]);

    useEffect(() => {
        if (
            reportType === 'benchmark' &&
            benchmarkQuarter &&
            institution &&
            benchmarkRSSDs.length >= 1
        ) {
            setIsBenchmarkDataLoading(true);

            const isInitialLoad = selectedMdrms.length === 0;
            const mdrmValue = isInitialLoad ? 'all' : selectedMdrms.join(', ');

            fetchBenchmarkData({
                variables: {
                    form: reportingForm,
                    institutionA: institution,
                    compareWith: benchmarkRSSDs.filter((r) => r !== institution),
                    quarter: formatQuarter(benchmarkQuarter),
                    case: valueMode,
                    mdrm: mdrmValue,
                },
            })
                .then(() => {
                    setIsBenchmarkDataLoading(false);
                })
                .catch(() => {
                    setIsBenchmarkDataLoading(false);
                });

        } else {
            setIsBenchmarkDataLoading(false);
        }
    }, [
        reportType,
        benchmarkQuarter,
        benchmarkRSSDs,
        institution,
        valueMode,
        selectedMdrms,
    ]);

    useEffect(() => {
        const timer = setTimeout(() => setLoadingTimeout(true), 15000);
        return () => clearTimeout(timer);
    }, [scatterRaw, mdrmData]);

    const isLoadingScatter =
        !scatterRaw?.[`${getPrefix()}scatterData`] && !loadingTimeout;

    const isLoadingMdrm =
        selectedMdrms.length > 0 &&
        !mdrmData?.[`${getPrefix()}mdrmData`] &&
        !loadingTimeout;
    const currentMdrmRef = useRef(null);

    const handleExplainMdrm = async (mdrm) => {
        setInsightMdrm(mdrm);
        setTypingIndex(0);
        setMdrmTyping('');
        setGeneratedData(null);
        currentMdrmRef.current = mdrm;

        const compareWith = benchmarkRSSDs.filter((r) => r !== institution);
        const rssidsToUse =
            valueMode === 'benchmark' ? [institution, ...compareWith] : [institution];

        try {
            const result = await executeMutation({
                variables: {
                    rssid: rssidsToUse,
                    mdrm,
                    quarter: formatQuarter(endQuarter),
                    reportingForm,
                },
            });

            setGeneratedData(result.data);
        } catch (err) {
            console.error('Mutation failed:', err);
        }
    };
    useEffect(() => {
        const raw =
            generatedData?.BENCHMARKgenerateQuarterValues ||
            generatedData?.DEMOgenerateQuarterValues ||
            generatedData?.BENCHMARKanalyzeAnomalyNarrative ||
            generatedData?.DEMOanalyzeAnomalyNarrative;

        if (typeof raw === 'string' && insightMdrm && mdrmTyping === '') {
            const clean = raw.replace(/^```markdown\s*/, '').replace(/\s*```$/, '');

            fullMarkdownRef.current = clean;
            typingIndexRef.current = 0;

            typingIntervalRef.current = setInterval(() => {
                setMdrmTyping((prev) => {
                    const i = typingIndexRef.current;
                    if (i < clean.length) {
                        typingIndexRef.current++;
                        return prev + clean.charAt(i);
                    } else {
                        clearInterval(typingIntervalRef.current);
                        return prev;
                    }
                });
            }, 10);
        }

        return () => clearInterval(typingIntervalRef.current);
    }, [generatedData, insightMdrm]);

    useEffect(() => {
        setIsHeatmapLoading(
            !(heatmapData && heatmapData.length) && selectedMdrms.length > 0,
        );
    }, [heatmapData, selectedMdrms]);

    useEffect(() => {
        const tableKey = `${getPrefix()}tableData`;
        if (tData?.data?.[tableKey]) {
            setTableData(tData.data[tableKey]);
            setIsTableLoading(false);
        }
    }, [tData.data]);

    const filteredTableData = useMemo(() => {
        if (!tableData) return [];

        const loweredKeys = (searchKeys || [])
            .map((k) => k.toLowerCase().trim())
            .filter(Boolean);

        return tableData.filter((item) => {
            const row = item.data || item;
            const schedule = row.Schedule || '';

            if (hiddenSchedules.has(schedule)) return false;

            if (loweredKeys.length === 0) return true;

            return loweredKeys.some(
                (key) =>
                    (row.MDRM || '').toLowerCase().includes(key) ||
                    (row.Schedule || '').toLowerCase().includes(key) ||
                    (row.ItemName || '').toLowerCase().includes(key),
            );
        });
    }, [tableData, hiddenSchedules, searchKeys]);

    const filteredBenchmarkTableData = useMemo(() => {
        if (!benchmarkTableData) return [];

        let filtered = benchmarkTableData.filter((item) => {
            const schedule = item.schedule || '';
            return !hiddenSchedules.has(schedule);
        });

        if (searchKeys.length > 0) {
            const loweredKeys = searchKeys
                .map((k) => k.toLowerCase().trim())
                .filter(Boolean);

            filtered = filtered.filter((item) => {
                const loweredItem = {
                    mdrm: (item.mdrm || '').toLowerCase(),
                    schedule: (item.schedule || '').toLowerCase(),
                    item_name: (item.item_name || '').toLowerCase(),
                };

                return loweredKeys.some((key) =>
                    ['mdrm', 'schedule', 'item_name'].some((col) =>
                        loweredItem[col].includes(key),
                    ),
                );
            });
        }

        if (selectedMdrms.length > 0) {
            const selectedSet = new Set(selectedMdrms.map((m) => m.toLowerCase()));
            filtered = filtered.filter((item) =>
                selectedSet.has((item.mdrm || '').toLowerCase()),
            );
        }

        return filtered;
    }, [benchmarkTableData, hiddenSchedules, searchKeys, selectedMdrms]);

    const filteredBenchmarkSchedules = useMemo(() => {
        if (!filteredBenchmarkTableData) return new Set();
        const set = new Set();
        filteredBenchmarkTableData.forEach((row) => {
            if (row.schedule) set.add(row.schedule);
        });
        return set;
    }, [filteredBenchmarkTableData]);

    const filteredScatterData = useMemo(() => {
        if (!scatterData) return [];

        let filtered = scatterData;

        if (searchKeys && searchKeys.length > 0) {
            const loweredKeys = searchKeys
                .map((k) => k.toLowerCase().trim())
                .filter(Boolean);

            filtered = filtered.filter((d) => {
                const dataItem = d.data || {};
                return loweredKeys.some(
                    (key) =>
                        (dataItem.MDRM || '').toLowerCase().includes(key) ||
                        (dataItem.Schedule || '').toLowerCase().includes(key) ||
                        (dataItem.ItemName || '').toLowerCase().includes(key),
                );
            });
        }

        if (selectedMdrms && selectedMdrms.length > 0) {
            const selectedSet = new Set(selectedMdrms.map((m) => m.toLowerCase()));

            filtered = filtered.filter((d) => {
                return selectedSet.has((d.data.MDRM || '').toLowerCase());
            });
        }

        return filtered;
    }, [scatterData, searchKeys, selectedMdrms]);

    const filteredTableSchedules = useMemo(() => {
        if (!filteredTableData) return new Set();
        const set = new Set();
        filteredTableData.forEach((row) => {
            const schedule = row.data?.Schedule || row.Schedule || '';
            if (schedule) set.add(schedule);
        });
        return set;
    }, [filteredTableData]);

    const filteredInstitutionDetails = useMemo(() => {
        if (!benchmarkInstitutionDetails) return {};
        if (!benchmarkRSSDs || benchmarkRSSDs.length === 0) return {};

        return benchmarkRSSDs.reduce((acc, rssd) => {
            if (benchmarkInstitutionDetails[rssd]) {
                acc[rssd] = benchmarkInstitutionDetails[rssd];
            }
            return acc;
        }, {});
    }, [benchmarkInstitutionDetails, benchmarkRSSDs]);

    const [
        fetchHeatmapVariations,
        { data: newHeatmapData, loading: isNewHeatmapLoading },
    ] = useLazyQuery(HEATMAP_VARIATIONS);

    useEffect(() => {
        if (!isInitialized || selectedMdrms.length === 0) {
            return;
        }

        fetchHeatmapVariations({
            variables: {
                form: reportingForm,
                rssid: institution,
                mdrms: selectedMdrms,
                startQuarter: formatQuarter(startQuarter),
                endQuarter: formatQuarter(endQuarter),
            },
        });
    }, [
        isInitialized,
        selectedMdrms,
        reportingForm,
        institution,
        startQuarter,
        endQuarter,
        fetchHeatmapVariations,
    ]);

    const prefix = React.useMemo(() => getPrefix(), [dataSource]);

    useEffect(() => {
        if (!newHeatmapData?.[`${prefix}HeatmapVariations`]) return;

        const heatmapResponse = newHeatmapData[`${prefix}HeatmapVariations`];
        setHeatmapColumns(heatmapResponse.periods || []);

        const trendlineData = heatmapResponse.rows.map((row) => {
            const values = heatmapResponse.periods.map((period) => {
                const cell = row.cells.find((c) => c.quarter === period);
                return {
                    quarter: period,
                    value: cell ? cell.value : 0,
                    color: cell?.color || '#ccc',
                };
            });

            return {
                mdrm: row.mdrm,
                values,
            };
        });

        setTrendLineChartData(trendlineData);

        const processedData = heatmapResponse.rows.flatMap((row) =>
            row.cells.map((cell) => ({
                row: `${row.mdrm} - ${row.itemName}`,
                mdrm: row.mdrm,
                schedule: row.schedule,
                column: cell.quarter,
                value: cell.value,
                normalized: cell.normalized,
                color: cell.color,
            })),
        );

        if (selectedMdrms.length > 0) {
            const mdrmOrderMap = new Map();
            selectedMdrms.forEach((mdrm, idx) => mdrmOrderMap.set(mdrm, idx));

            const normalize = (s) => s.trim().toLowerCase();

            const sortedProcessedData = processedData.slice().sort((a, b) => {
                const aMdrm = normalize(a.row.split(' - ')[0]);
                const bMdrm = normalize(b.row.split(' - ')[0]);

                const aOrder = mdrmOrderMap.has(aMdrm)
                    ? mdrmOrderMap.get(aMdrm)
                    : Infinity;
                const bOrder = mdrmOrderMap.has(bMdrm)
                    ? mdrmOrderMap.get(bMdrm)
                    : Infinity;

                return aOrder - bOrder;
            });

            setHeatmapChartData(sortedProcessedData);
        } else {
            setHeatmapChartData(processedData);
        }
    }, [newHeatmapData, selectedMdrms, prefix]);

    useEffect(() => {
        if (!formDetails.rssids || formDetails.rssids.length === 0) {
            setBenchmarkRSSDs([]);
            return;
        }

        const options = formDetails.rssids.map(item => item.split(' - ')[0]);
        if (!options.length) {
            setBenchmarkRSSDs([]);
            return;
        }

        const locked = institution || options[0];
        let secondVal = null;

        if (options.length > 1) {
            secondVal = options.find(val => val !== locked);
        }

        const initialBenchmark = secondVal ? [locked, secondVal] : [locked];
        setBenchmarkRSSDs(initialBenchmark);
    }, [reportingForm, institution, formDetails.rssids]);

    if (metaLoading) return <DashboardSkeleton />;

    const updateQueryParams = (form, institution, start, end) => {
        if (form && institution && start && end) {
            setQueryParams({
                reportingForm: form,
                institution,
                startQuarter: start,
                endQuarter: end,
            });
            setSelectedMdrms([]);
            setSelectedMDRM(null);
        }
    };

    const handleRssidStarClick = async ({
        rssid,
        startQuarter,
        endQuarter,
        reportingForm,
    }) => {
        setInsightMdrm(`RSSID: ${rssid}`);
        setTypingIndex(0);
        setMdrmTyping('');
        setGeneratedData(null);

        try {
            const result = await runAnalyzeNarrative({
                variables: {
                    rssid,
                    currentQuarterCombination: [
                        formatQuarter(startQuarter),
                        formatQuarter(endQuarter),
                    ],
                    reportingForm,
                },
            });
            setGeneratedData(result.data);
        } catch (err) {
            console.error('Narrative Mutation Failed', err);
        }
    };

    const handleRemoveMdrm = (mdrmToRemove) => {
        setIsTableLoading(true);
        setSelectedMdrms((prev) => prev.filter((mdrm) => mdrm !== mdrmToRemove));
    };

    const handleRemoveAllMdrms = () => {
        if (selectedMdrms.length === 0) return;
        setIsTableLoading(true);
        setSelectedMdrms([]);
        setSelectedMDRM(null);
    };

    const showScatterPlotSkeleton = isLoadingScatter || isScatterLoading || isBenchmarkDataLoading;

    const showTableSkeleton = isTableQueryLoading || isTableLoading || isBenchmarkDataLoading;

    const getInstituteNameByRssid = (rssid) => {
        if (!formDetails.rssids) return null;
        const match = formDetails.rssids.find(item => item.startsWith(rssid));
        if (!match) return null;
        return match.split(' - ')[1]?.trim() || null;
    };

    const getFileName = () => {
        const today = new Date().toISOString().slice(0, 10);

        if (reportType === VARIATIONS) {
            const instituteNameRaw = getInstituteNameByRssid(institution) || institution || 'Institute';
            const instituteNameSafe = instituteNameRaw.replace(/\s+/g, '_').replace(/[^\w-]/g, '');

            const formatQuarterForFileName = (qtr) => {
                if (!qtr) return '';
                return qtr
                    .trim()
                    .replace(/^q/i, 'Q')
                    .replace(/[- ]/g, '_');
            };

            const startQ = formatQuarterForFileName(startQuarter) || 'StartQuarter';
            const endQ = formatQuarterForFileName(endQuarter) || 'EndQuarter';

            return `${instituteNameSafe}_${startQ}_vs_${endQ}_${today}`;
        }

        if (reportType === BENCHMARK) {
            const lockedInstituteNameRaw = benchmarkInstitutionDetails?.[institution] || institution || 'LockedInstitute';
            const lockedInstituteNameSafe = lockedInstituteNameRaw.replace(/\s+/g, '_').replace(/[^\w-]/g, '');

            const otherRSSDs = benchmarkRSSDs.filter((rssd) => rssd !== institution);
            const otherRSSDsSafe = otherRSSDs.map(rssd => rssd.replace(/\s+/g, ''));

            const benchmarkPart = [lockedInstituteNameSafe, ...otherRSSDsSafe].join('_vs_');

            return `${benchmarkPart}_${today}`;
        }

        return `TableData_${reportType}_${today}`;
    };

    const handleExportXLS = () => {
        if (reportType === BENCHMARK) {
            if (!filteredBenchmarkTableData || filteredBenchmarkTableData.length === 0) {
                alert('No benchmark data to export');
                return;
            }

            const institutionsSet = new Set();
            filteredBenchmarkTableData.forEach(item => {
                if (item.institution_a?.rssid) {
                    institutionsSet.add(item.institution_a.rssid);
                }
                (item.compare_with || []).forEach(c => {
                    if (c?.rssid) institutionsSet.add(c.rssid);
                });
            });

            const institutions = Array.from(institutionsSet);

            const institutionColumns = institutions.map(rssd => ({
                rssd,
                name: benchmarkInstitutionDetails[rssd] || rssd,
            }));

            const headers = [
                'mdrm',
                'schedule',
                'lineItemCode',
                'itemName',
                ...institutionColumns,
                'absoluteDifferencePercent',
                'differencePercent',
            ];

            const filename = getFileName();
            exportToXLS(filteredBenchmarkTableData, reportType, filename, headers);
        } else {
            const filename = getFileName();
            exportToXLS(filteredTableData, reportType, filename);
        }
    };

    const isExportDisabled =
        (reportType === BENCHMARK ? filteredBenchmarkTableData.length === 0 : filteredTableData.length === 0) ||
        searchKeys.length > 0 ||
        selectedMdrms.length > 0;

    return (
        <div className="report-inspector">
            <div className="report-inspector-tabs">
                <span className="report-inspector-tabs-button">Report Type:</span>
                {['variations', 'benchmark'].map((type) => (
                    <button
                        key={type}
                        type="button"
                        onClick={() => {
                            setReportType(type);
                            setTableNeedsRefresh(true);

                            if (type === 'benchmark' && formDetails) {
                                if (!benchmarkQuarter) {
                                    const defaultQuarter = formDetails.StartQuarter[0];
                                    if (defaultQuarter) {
                                        setBenchmarkQuarter(defaultQuarter);
                                    }
                                }
                            }
                        }}
                        className={`report-inspector-tab-button ${reportType === type ? 'is-active' : ''}`}
                    >
                        {type === 'variations' ? 'Variations' : 'Benchmark'}
                    </button>
                ))}
            </div>

            <Header
                reportingForm={reportingForm}
                institution={institution}
                startQuarter={startQuarter}
                endQuarter={endQuarter}
                formsList={formDetails.forms || []}
                rssdList={formDetails.rssids || []}
                startQuarterList={formDetails.StartQuarter || []}
                endQuarterList={formDetails.EndQuarter || []}
                reportType={reportType}
                benchmarkRSSDs={benchmarkRSSDs}
                onFormChange={(form) => {
                    setReportingForm(form);
                }}
                onInstitutionChange={(institution) => {
                    setInstitution(institution);
                    updateQueryParams(
                        reportingForm,
                        institution,
                        startQuarter,
                        endQuarter,
                    );
                }}
                onStartQuarterChange={(startQuarter) => {
                    setStartQuarter(startQuarter);
                    updateQueryParams(
                        reportingForm,
                        institution,
                        startQuarter,
                        endQuarter,
                    );
                }}
                onEndQuarterChange={(endQuarter) => {
                    setEndQuarter(endQuarter);
                    updateQueryParams(
                        reportingForm,
                        institution,
                        startQuarter,
                        endQuarter,
                    );
                }}
                onBenchmarkQuarterChange={setBenchmarkQuarter}
                onBenchmarkRSSDChange={setBenchmarkRSSDs}
                onStarClick={handleRssidStarClick}
            />

            <div className="report-inspector-value">
                <span className="report-inspector-label-value">Value Type:</span>

                {['regular', 'special'].map((mode) => (
                    <button
                        key={mode}
                        type="button"
                        onClick={() => setValueMode(mode)}
                        className={`report-inspector-tab-button-value ${valueMode === mode ? 'is-active' : ''}`}
                    >
                        {mode === 'regular' ? 'Regular Values' : 'Special Values'}
                    </button>
                ))}
            </div>

            <div className="download-container">
                <button
                    onClick={handleExportXLS}
                    disabled={isExportDisabled}
                    style={{
                        padding: '8px 16px',
                        backgroundColor: isExportDisabled ? '#ccc' : '#e0301e',
                        color: isExportDisabled ? '#666' : 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: isExportDisabled ? 'not-allowed' : 'pointer',
                        fontWeight: 'bold',
                    }}
                >
                    Export {!isExportDisabled && <span>⬇️</span>}
                </button>
            </div>

            {isInitialized && (
                <div className="report-wrapper">
                    <div className="row row-flex">
                        <Widget title="Scatter Plot" className="widget-wrapper">
                            {showScatterPlotSkeleton ? (
                                <ScatterPlotSkeleton />
                            ) : reportType === 'benchmark' ? (
                                <BenchmarkScatterPlot
                                    data={filteredBenchmarkTableData}
                                    fullData={benchmarkTableData}
                                    selectedMDRM={selectedMDRM}
                                    selectMode={selectMode}
                                    hiddenSchedules={hiddenSchedules}
                                    setHiddenSchedules={setHiddenSchedules}
                                    filteredTableSchedules={filteredBenchmarkSchedules}
                                    onPointClick={setSelectedMDRM}
                                    onResetSelection={handleRemoveAllMdrms}
                                    onAreaSelect={(area) => {
                                        if (!area) return;

                                        const selected = benchmarkTableData
                                            .filter((d) => {
                                                const val = parseFloat(
                                                    d?.difference_percent,
                                                );
                                                const compareVals = [
                                                    ...(d.compare_with || []),
                                                    d.institution_a,
                                                ]
                                                    .map((i) => parseFloat(i?.value))
                                                    .filter((v) => !isNaN(v));
                                                const avg = compareVals.length
                                                    ? compareVals.reduce(
                                                        (a, b) => a + b,
                                                        0,
                                                    ) / compareVals.length
                                                    : 0;
                                                return (
                                                    val >= area.x0 &&
                                                    val <= area.x1 &&
                                                    avg >= area.y0 &&
                                                    avg <= area.y1
                                                );
                                            })
                                            .map((d) => d.mdrm);
                                        setIsTableLoading(true);
                                        setSelectedMdrms(
                                            selected.length > 0 ? selected : [],
                                        );
                                    }}
                                />
                            ) : valueMode === 'special' ? (
                                <SpecialScatterPlot
                                    data={filteredScatterData}
                                    fullData={scatterData}
                                    filteredTableSchedules={filteredTableSchedules}
                                    onAreaSelect={(selected) => {
                                        setIsTableLoading(true);
                                        setSelectedMdrms(
                                            selected.length > 0 ? selected : [],
                                        );
                                    }}
                                    hiddenSchedules={hiddenSchedules}
                                    setHiddenSchedules={setHiddenSchedules}
                                    onResetSelection={handleRemoveAllMdrms}
                                />
                            ) : (
                                <ScatterPlotWrapper
                                    data={filteredScatterData}
                                    fullData={scatterData}
                                    filteredTableSchedules={filteredTableSchedules}
                                    selectedMDRM={selectedMDRM}
                                    onPointClick={setSelectedMDRM}
                                    selectMode={selectMode}
                                    setSelectMode={setSelectMode}
                                    hiddenSchedules={hiddenSchedules}
                                    setHiddenSchedules={setHiddenSchedules}
                                    onResetSelection={handleRemoveAllMdrms}
                                    onAreaSelect={(area) => {
                                        if (!area) return;
                                        const selected = scatterData
                                            .filter((d) => {
                                                const x = d.data.AbsoluteChangePercent;
                                                const y = d.data.E_value;
                                                return (
                                                    x >= area.x0 &&
                                                    x <= area.x1 &&
                                                    y >= area.y0 &&
                                                    y <= area.y1
                                                );
                                            })
                                            .map((d) => d.data.MDRM);
                                        setIsTableLoading(true);
                                        setSelectedMdrms(
                                            selected.length > 0 ? selected : [],
                                        );
                                    }}
                                />
                            )}
                        </Widget>

                        <Widget title="Table" className="table-widget">
                            {showTableSkeleton ? (
                                <TableSkeleton />
                            ) : (
                                <TableWrapper
                                    data={filteredTableData}
                                    benchmarkData={filteredBenchmarkTableData}
                                    institutionDetails={filteredInstitutionDetails}
                                    reportType={reportType}
                                    onExplainMdrm={handleExplainMdrm}
                                    onMdrmFilterChange={setSelectedMdrms}
                                    onSearchKeysChange={handleSearchKeysChange}
                                    searchKeys={searchKeys}
                                    benchmarkRSSDs={benchmarkRSSDs}
                                    startQuarter={startQuarter}
                                    endQuarter={endQuarter}
                                    lockedInstitute={institution}
                                />
                            )}
                        </Widget>
                    </div>

                    {insightMdrm && (
                        <div
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                width: '100vw',
                                height: '100vh',
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 1000,
                            }}
                            onClick={() => setInsightMdrm(null)}
                        >
                            <div
                                style={{
                                    background: '#fff',
                                    borderRadius: '12px',
                                    padding: '24px',
                                    width: '800px',
                                    maxHeight: '80vh',
                                    overflowY: 'auto',
                                    position: 'relative',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <button
                                    onClick={() => setInsightMdrm(null)}
                                    style={{
                                        position: 'absolute',
                                        top: '10px',
                                        right: '12px',
                                        background: 'transparent',
                                        border: 'none',
                                        fontSize: '18px',
                                        cursor: 'pointer',
                                    }}
                                >
                                    ×
                                </button>

                                <div style={{ flexGrow: 1 }}>
                                    <div style={{ flexGrow: 1 }}>
                                        {mdrmTyping.length === 0 ? (
                                            <div className="loader-container">
                                                <Loader />
                                            </div>
                                        ) : (
                                            <>
                                                <Markdown>{mdrmTyping}</Markdown>
                                                {mdrmTyping.length <
                                                    fullMarkdownRef.current.length && (
                                                        <div
                                                            style={{
                                                                textAlign: 'right',
                                                                marginBottom: '10px',
                                                            }}
                                                        >
                                                            <button
                                                                onClick={() =>
                                                                    setMdrmTyping(
                                                                        fullMarkdownRef.current,
                                                                    )
                                                                }
                                                                style={{
                                                                    background: '#eee',
                                                                    border: '1px solid #ccc',
                                                                    borderRadius: '6px',
                                                                    padding: '4px 8px',
                                                                    cursor: 'pointer',
                                                                    fontSize: '12px',
                                                                }}
                                                            >
                                                                Skip Animation
                                                            </button>
                                                        </div>
                                                    )}
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div
                                    style={{
                                        marginTop: '20px',
                                        fontSize: '12px',
                                        color: '#888',
                                        borderTop: '1px solid #ddd',
                                        paddingTop: '12px',
                                    }}
                                >
                                    AI-generated content may be inaccurate, requires human
                                    review, and is not intended to be legal, accounting,
                                    tax, or other professional advice.
                                </div>
                            </div>
                        </div>
                    )}

                    {(selectedMdrms.length > 0 && reportType !== 'benchmark') && (
                        <SelectedMdrms
                            mdrms={selectedMdrms}
                            onRemove={handleRemoveMdrm}
                            onRemoveAll={handleRemoveAllMdrms}
                        />
                    )}

                    {selectedMdrms.length > 0 && (
                        <div className="row col-flex">
                            {reportType === 'variations' && (
                                <>
                                    <Widget title="Heatmap" className="widget-wrapper">
                                        {isLoadingMdrm || isNewHeatmapLoading ? (
                                            <HeatmapSkeleton />
                                        ) : (
                                            <HeatmapWrapper
                                                data={heatmapChartData}
                                                columns={heatmapColumns}
                                            />
                                        )}
                                    </Widget>
                                    <Widget title="Trend Line" className="widget-wrapper">
                                        {isLoadingMdrm || isNewHeatmapLoading ? (
                                            <TrendLineSkeleton />
                                        ) : (
                                            <TrendLineWrapper
                                                data={trendLineChartData}
                                                periods={heatmapColumns}
                                                type="Variations"
                                            />
                                        )}
                                    </Widget>
                                </>
                            )}
                            {/* {reportType === 'benchmark' && (
                                <Widget title="Trend Line" className="widget-wrapper">
                                    {isLoadingMdrm || isHeatmapLoading ? (
                                        <TrendLineSkeleton />
                                    ) : (
                                        <TrendLineWrapper
                                            data={trendData}
                                            type="Benchmark"
                                        />
                                    )}
                                </Widget>
                            )} */}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ReportDashboard;
